import {createStore, filterNil} from '@ngneat/elf';
import {deleteEntities, selectEntity, selectManyByPredicate, updateEntities, withEntities} from "@ngneat/elf-entities";
import {computed, Injectable, Signal, signal} from "@angular/core";
import {erstatteAlleEntiteterMedForelder, FeilhåndteringArgumenter, Feilhåndteringsstrategi, FeilhåndteringsstrategiForDataOgMeta, forelderIdPredikat, hentFraLokallagerEllerNull, leggTilForelderId, Leveringsvalg, oppdaterEntitetMedForelder, opprettEllerOppdatertHendelse, registrerEntitetMedForelder, serverFraLagerOgRequestPakketIMeta, serverFraRequest, serverFraRequestPakketIMeta, slettAlleEntiteterMedForelder, slettAlleEntiteterMedForeldre, slettHendelse, somFeilhåndteringsstrategiForDataOgMeta, stdFeilhåndteringsstrategi, stdFeilhåndteringsstrategiForDataOgMeta, STRATEGI_TYPE, visFeilmeldingVhjaMeldingTilBruker} from "felles-ng2/tjenester/datalager/datatjeneste";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {AgendaelementId, AgendaelementModell, ArkivMappenodeModell, BeslutningModell, FavorittRessursClient, FavorittType, KopierTilMøterSpesifikasjon, Modelloppdatering, ModelloppdateringType, MøteId, MøteModell, MøteOgNySakModell, OppdateringmodellEntitetMedForelder, OppdateringmodellSakFlyttetTilMøte, OppdaterPauseSpesifikasjon, OppdaterSakMedOpsjonerSpesifikasjon, OpprettSakerSpesifikasjon, SakFlyttetModell, SakRessursClient, SaksreferatSpesifikasjon, SkjermingDetaljerModell, SkjermingModell} from "../ressurser/generertNg2HttpKlient";
import {DatalagerAbonnenter} from "felles-ng2/tjenester/datalager/DatalagerAbonnenter";
import {erIkkeNil, erNil} from "../../støttefunksjoner/nilsjekk";
import {firstValueFrom, Observable} from "rxjs";
import {DataOgMeta, mapTilData} from "./DataOgMeta";
import {HttpResponse} from "@angular/common/http";
import {tap} from "rxjs/operators";
import {MøteDatatjeneste} from "felles-ng2/tjenester/datalager/MøteDatatjeneste";
import {sorterSak} from "felles-ng2/modell/sakModellHjelpere";
import {ModellOpprettetEllerOppdatertHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellOpprettetEllerOppdatertHendelseHåndteringBygger";
import {ModellSlettetHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellSlettetHendelseHåndteringBygger";
import {SaksdokumentDatatjeneste} from "felles-ng2/tjenester/datalager/SaksdokumentDatatjeneste";
import {sorterLevertSamling, utelatFørsteDersomNil} from "felles/ng2/rxjsHjelpere";
import {KommentarDatatjeneste} from "felles-ng2/tjenester/datalager/KommentarDatatjeneste";
import {distinctUntilChangedDeep} from "felles-ng2/støttefunksjoner/observable";
import {OppgaveDatatjeneste} from "felles-ng2/tjenester/datalager/OppgaveDatatjeneste";
import {BeslutningDatatjeneste} from "felles-ng2/tjenester/datalager/BeslutningDatatjeneste";
import {i18nArgumenterMedAnsvarligBruker} from "felles-ng2/modell/modelloppdateringModellHjelpere";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {MøteMedAggregaterDatatjeneste} from "felles-ng2/tjenester/datalager/MøteMedAggregaterDatatjeneste";
import {finnMøteIdBlantForeldre, Modelloppdateringer, OppdateringmodellNySak, OppdateringmodellSakOppdatert, OppdateringmodellSakReferatOppdatert, OppdateringmodellSakSlettet} from "felles-ng2/tjenester/Modelloppdateringer";
import {FavorittDatatjeneste} from "felles-ng2/tjenester/datalager/FavorittDatatjeneste";
import {AvstemningDatatjeneste} from "felles-ng2/tjenester/datalager/AvstemningDatatjeneste";

const debug = initDebugger('SakDatatjeneste');

/** Datalager for {@link AgendaelementModell} */
@Injectable() //tilgjengeliggjøres via DatalagerModul
export class SakDatatjeneste {

    /** Aktiv sak */
    private readonly _aktivSakId = signal<AgendaelementId | null>(null);
    private readonly _erAktivSakSatt = computed(() => erIkkeNil(this.aktivSakIdSomSignal()));

    /** Lager for {@link AgendaelementModell}. Bruker {@link MøteId} som forelder. */
      //public for testbarhet
    readonly sakLager = createStore({name: 'sak'}, withEntities<AgendaelementModell>());

    /** Holder rede på åpne subscriptions basert på sak.id. Benyttes ifbm modelloppdateringer. */
    readonly sakAbonnenter = new DatalagerAbonnenter<AgendaelementId>('sak'); //public for testbarhet

    /** Holder rede på åpne subscriptions basert på møteId. Benyttes ifbm modelloppdateringer. */
    readonly møteAbonnenter = new DatalagerAbonnenter<MøteId>('sak.møte'); //public for testbarhet

    // datatjenestene under må settes etter opprettelse av instans pga sirkularitet
    private møteDatatjeneste!: MøteDatatjeneste;

    constructor(private sakRessurs: SakRessursClient,
                private saksdokumentDatatjeneste: SaksdokumentDatatjeneste,
                private oppgaveDatatjeneste: OppgaveDatatjeneste,
                private beslutningDatatjeneste: BeslutningDatatjeneste,
                private avstemningDatatjeneste: AvstemningDatatjeneste,
                private kommentarDatatjeneste: KommentarDatatjeneste,
                private møteMedAggregaterDatatjeneste: MøteMedAggregaterDatatjeneste,
                private favorittDatatjeneste: FavorittDatatjeneste,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private oversettelser: Oversettelser,
                private modelloppdateringer: Modelloppdateringer) {
        this.propagerEndringerTilAndreDatatjenester();
        this.lyttTilModelloppdateringer();
    }

    /** For intern bruk. Datatjenestene under må settes etter opprettelse av instans pga sirkularitet */
    setDatatjenester(møteDatatjeneste: MøteDatatjeneste) {
        this.møteDatatjeneste = møteDatatjeneste;
    }

    get aktivSakId(): AgendaelementId | null {
        return this._aktivSakId();
    }

    get aktivSakIdSomSignal(): Signal<AgendaelementId | null> {
        return this._aktivSakId;
    }

    /** Setter angitt id som aktiv sak.
     * Returnerer AgendaelementModell tilknyttet angitt id i lokallageret, null dersom lokallageret ikke inneholder angitt sak  */
    setAktivSak(id: AgendaelementId): AgendaelementModell | null {
        debug(`setAktivSak id:${id} (id var :${this.aktivSakId})`)
        this._aktivSakId.set(id);
        return this.sakLager.value.entities[id] ?? null;
    }

    get erAktivSakSatt(): boolean {
        return this.erAktivSakSattSomSignal();
    }

    get erAktivSakSattSomSignal(): Signal<boolean> {
        return this._erAktivSakSatt;
    }

    resetAktivSak() {
        debug(`resetAktivSak (id var :${this.aktivSakId})`)
        this._aktivSakId.set(null);
    }

    /** Returnerer angitt sak fra lokallager. Returnerer null dersom ikke funnet */
    hentFraLokallager(id: AgendaelementId): AgendaelementModellDLM | null {
        return hentFraLokallagerEllerNull(this.sakLager, id);
    }

    /** Returnerer observerbar som levere saker knyttet til angitt møte fra lokallager.
     * Leverer også når et skjer endringer knyttet til angitt møte i lokallager */
    finnTilknyttetMøteFraLokallager(møteId: MøteId): Observable<AgendaelementModellDLM[]> {
        debug('finnTilknyttetMøteFraLokallager', møteId, this.møteAbonnenter.antallAbonnenter(møteId))
        return this.møteAbonnenter.observer(møteId,
          this.sakLager.pipe(
            selectManyByPredicate(forelderIdPredikat(møteId)),
            sorterLevertSamling(sorterSak),
            distinctUntilChangedDeep() //kun lever verdier fra lageret ved reelle endringer
          ));
    }

    /** Laster angitt sak.
     * Merk at dersom leveringsvalg = lever_undefined_ved_slettet vil returnert observable levere 'undefined' når forespurt entitet slettes i lageret */
    hentMedMeta(id: AgendaelementId, leveringsvalg?: Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi?: FeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<AgendaelementModellDLM>>;
    hentMedMeta(id: AgendaelementId, leveringsvalg: Leveringsvalg, feilhåndteringsstrategi?: FeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<AgendaelementModellDLM | undefined>>;
    hentMedMeta(id: AgendaelementId, leveringsvalg = Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<AgendaelementModellDLM | undefined>> {
        return this.sakAbonnenter.observer(id,
          serverFraLagerOgRequestPakketIMeta({
              ressurskall: () => this.sakRessurs.hent(id),
              registrerILager: (respons: HttpResponse<AgendaelementModell>) => registrerEntitetMedForelder(this.sakLager, respons.body!, this.forelderIdFraSak(respons.body!)),
              hentFraLager: () => this.sakLager.pipe(
                selectEntity(id),
                leveringsvalg == Leveringsvalg.ikke_lever_ved_slettet ? filterNil() : utelatFørsteDersomNil(),
                distinctUntilChangedDeep()), //kun lever verdier fra lageret ved reelle endringer
              ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke hente sak')), 'hentMedMeta')
          }));
    }

    /** Laster angitt sak.
     * Merk at dersom leveringsvalg = lever_undefined_ved_slettet vil returnert observable levere 'undefined' når forespurt entitet slettes i lageret */
    hent(id: AgendaelementId, leveringsvalg?: Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi?: Feilhåndteringsstrategi): Observable<AgendaelementModellDLM>
    hent(id: AgendaelementId, leveringsvalg: Leveringsvalg, feilhåndteringsstrategi?: Feilhåndteringsstrategi): Observable<AgendaelementModellDLM | undefined>
    hent(id: AgendaelementId, leveringsvalg = Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<AgendaelementModellDLM | undefined> {
        return this.hentMedMeta(id, leveringsvalg, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    /** Oppdaterer angitt sak i lokallager med verdier angitt i `oppdatering`. */
    oppdaterILokallager(sakId: AgendaelementId, oppdatering: Partial<AgendaelementModell>) {
        this.sakLager.update(updateEntities(sakId, oppdatering));
    }

    /** Oppdaterer angitt sak i lokallager med angitt skjermingsinformasjon. */
    oppdaterSkjermingILokallager(sakId: AgendaelementId, skjerming: SkjermingModell) {
        const sak = hentFraLokallagerEllerNull(this.sakLager, sakId);
        if (erNil(sak)) return;
        this.oppdaterILokallager(sakId, {tilgang: {...sak.tilgang, skjerming: skjerming}});
    }

    /** Erstatter alle saker knytte til angitt møteId i lokallager */
    erstattSakerILokallager(møteId: MøteId, saker: AgendaelementModell[]) {
        erstatteAlleEntiteterMedForelder(this.sakLager, saker, møteId);
    }

    /** Sletter alle saker knytte til angitt møteId i lokallager */
    slettSakerTilknyttetMøteILokallager(møteId: MøteId) {
        slettAlleEntiteterMedForelder(this.sakLager, møteId);
    }

    /** Sletter alle saker knytte til angitte møteIder i lokallager */
    slettSakerTilknyttetMøterILokallager(møteIder: MøteId[]) {
        slettAlleEntiteterMedForeldre(this.sakLager, møteIder);
    }

    opprettSaker(spesifikasjon: OpprettSakerSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<MøteModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.sakRessurs.opprettSakerMedOpsjoner(spesifikasjon).råRespons()
              .pipe(tap(respons => {
                  const oppdatertMøte = respons.body!;
                  //møteDatatjeneste.erstattMøteILokallager(..) vil føre til at erstattSakerILokallager(..) blir kalt via propagering av lager-hendelser
                  this.møteDatatjeneste.erstattMøteILokallager(oppdatertMøte);
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere saker')), 'opprettSaker')
        }));
    }

    oppdaterSak(id: AgendaelementId, spesifikasjon: OppdaterSakMedOpsjonerSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<AgendaelementModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.sakRessurs.oppdaterMedOpsjoner(id, spesifikasjon).råRespons()
              .pipe(tap(respons => oppdaterEntitetMedForelder(this.sakLager, respons.body!, this.forelderIdFraSak(respons.body!)))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdatere sak')), 'oppdaterSak')
        }));
    }

    oppdaterPause(id: AgendaelementId, spesifikasjon: OppdaterPauseSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<AgendaelementModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.sakRessurs.oppdaterPause(id, spesifikasjon).råRespons()
              .pipe(tap(respons => oppdaterEntitetMedForelder(this.sakLager, respons.body!, this.forelderIdFraSak(respons.body!)))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdatere pause')), 'oppdaterPause')
        }));
    }

    oppdaterReferatOgBeslutninger(spesifikasjon: SaksreferatSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<AgendaelementModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.sakRessurs.oppdaterReferatOgBeslutninger(spesifikasjon.sakId, spesifikasjon).råRespons()
              .pipe(tap(respons => oppdaterEntitetMedForelder(this.sakLager, respons.body!, this.forelderIdFraSak(respons.body!)))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdatere referat')), 'oppdaterReferatOgBeslutninger')
        }));
    }

    /** Registrerer angitt sak som brukers favoritt dersom ikke allerede markert som favoritt,
     * eller fjernes angitt sak som brukers favoritt  */
    async endreFavorittmarkering(sak: AgendaelementModellDLM): Promise<void> {
        const favoritterOppdatert = await this.favorittDatatjeneste.endreFavorittmarkering(sak, FavorittType.sak);
        this.sakLager.update(updateEntities(sak.id, {favoritter: favoritterOppdatert}));
    }

    kopierTilMøter(kildesakId: AgendaelementId, spesifikasjon: KopierTilMøterSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<MøteOgNySakModell[]>> {
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.sakRessurs.kopierTilMøter(kildesakId, spesifikasjon).råRespons()
              .pipe(tap(respons => {
                  const nyeSaker = respons.body!;
                  //møteDatatjeneste.erstattMøteILokallager(..) vil føre til at erstattSakerILokallager(..) blir kalt via propagering av lager-hendelser
                  nyeSaker.forEach(nySak => this.møteDatatjeneste.erstattMøteILokallager(nySak.moete));
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke kopiere saker')), 'kopierTilMøter')
        }));
    }

    async flyttTilMøte(id: AgendaelementId, møteId: MøteId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<SakFlyttetModell> {
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.sakRessurs.flyttTilMøte(id, møteId).råRespons()
              .pipe(tap(respons => {
                  const oppdatertSak = respons.body!;
                  //møteDatatjeneste.erstattMøteILokallager(..) vil føre til at erstattSakerILokallager(..) blir kalt via propagering av lager-hendelser
                  this.møteDatatjeneste.erstattMøteILokallager(oppdatertSak.opprineligMoete);
                  this.møteDatatjeneste.erstattMøteILokallager(oppdatertSak.nyttMoete);
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke flytte sak')), 'flyttTilMøte')
        }));
    }

    async slett(id: AgendaelementId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<void> {
        await firstValueFrom(serverFraRequest({
            ressurskall: () => this.sakRessurs.slett(id).råRespons()
              .pipe(tap(respons => this.sakLager.update(deleteEntities(id)))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke slette sak')), 'slett')
        }));
    }


    //hjelpemetoder

    private forelderIdFraSak(sak: AgendaelementModellDLM) {
        return sak.moete.id;
    }

    private feilhåndteringArgumenter<RET_TYPE, STRATEGI extends STRATEGI_TYPE>(
      feilhåndteringsstrategi: STRATEGI,
      brukerrettetFeilmelding: string,
      metodeNavnForDebug: string): FeilhåndteringArgumenter<RET_TYPE, STRATEGI> {
        return {
            feilhåndteringsstrategi,
            visFeilmelding: visFeilmeldingVhjaMeldingTilBruker(brukerrettetFeilmelding, this.meldingTilBruker),
            dataReturnertVedFeil: null,
            debug: (formatter: any, ...args: any[]): void => debug(`${metodeNavnForDebug}: ${formatter}`, args),
        }
    }

    /** Ved endringer i sak må lager som håndterer data inkludert i sak oppdateres */
    private propagerEndringerTilAndreDatatjenester() {
        opprettEllerOppdatertHendelse(this.sakLager).subscribe(({hendelse, entiteter}) => {
            debug(`propagerer sakLager ${hendelse.type} til relatert datatjenester (dok/oppg/besl/kom/møteAgr) for sakene ${hendelse.ids}`, hendelse, entiteter);
            entiteter
              .filter(sak => !sak.erPause)
              .forEach(async sak => {
                  this.saksdokumentDatatjeneste.erstattDokumenterILokallager(sak.dokumenter, sak.id);
                  this.oppgaveDatatjeneste.erstattOppgaverTilknyttetSakILokallager(sak.oppgaver, sak.id);
                  this.beslutningDatatjeneste.erstattBeslutningerTilknyttetSakILokallager(sak.beslutninger, sak.id);
                  this.avstemningDatatjeneste.erstattAvstemningerTilknyttetSakILokallager(sak.avstemninger, sak.id);
                  this.kommentarDatatjeneste.erstattILokallager(sak.kommentarer, sak.id);
                  //propager til møteMedAggregaterDatatjeneste for å holde tellere i MøteMedAggregaterModell i synk
                  this.møteMedAggregaterDatatjeneste.oppdaterAntallSakerIILokallager(sak.moete.id, await (firstValueFrom(this.finnTilknyttetMøteFraLokallager(sak.moete.id))));
              });
        });

        slettHendelse(this.sakLager).subscribe(hendelse => {
            debug(`propagerer sakLager ${hendelse.type} til relatert datatjenester (dok/oppg/besl/kom/møteAgr) for sakene ${hendelse.ids}`);
            this.saksdokumentDatatjeneste.slettDokumenterTilknyttetSakerILokallager(hendelse.ids);
            this.oppgaveDatatjeneste.slettOppgaverTilknyttetSakerILokallager(hendelse.ids);
            this.beslutningDatatjeneste.slettBeslutningerTilknyttetSakerILokallager(hendelse.ids);
            this.avstemningDatatjeneste.slettAvstemningerTilknyttetSakerILokallager(hendelse.ids);
            this.kommentarDatatjeneste.slettKommentarerTilknyttetForelderILokallager(hendelse.ids);
            //kan ikke propagere endring til møteMedAggregaterDatatjeneste - mangler info om møteId
        });
    }

    /**
     * Lytt til relevante modelloppdateringer via {@link Modelloppdateringer} (push-baserte oppdateringer).
     * Merk at vi aldri avregistrerer subscription vi oppretter her da data-tjenesten aldri termineres.
     */
    private lyttTilModelloppdateringer() {
        this.lyttTilNySak();
        this.lyttTilSakOppdatert();
        this.lyttTilSaksreferatOppdatert();
        this.lyttTilSakFlyttetTilMøte();
        this.lyttTilSakSlettet();
        //håndtering av SAKSREKKEFØLGE_OPPDATERT skjer i MøteDatatjeneste
    }

    private lyttTilNySak() {
        this.opprettetEllerOppdatertGeneriskHendelse<OppdateringmodellNySak>(ModelloppdateringType.NY_SAK)
          .medAbonnentoppslag(this.sakAbonnenter, hendelseModell => hendelseModell.endretEntitet.id)
          .medAbonnentoppslagOpt(this.møteAbonnenter, finnMøteIdBlantForeldre)
          .medEtterLagerOppdatert(oppdatering => this.visMelding(i18n('Ny sak "{sak}" opprettet (av {bruker})'), this.i18nArgumenter(oppdatering, oppdatering.modell.endretEntitet.id)))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilSakOppdatert() {
        this.opprettetEllerOppdatertGeneriskHendelse<OppdateringmodellSakOppdatert>(ModelloppdateringType.SAK_OPPDATERT)
          .medAbonnentoppslag(this.sakAbonnenter, hendelseModell => hendelseModell.endretEntitet.id)
          .medAbonnentoppslagOpt(this.møteAbonnenter, finnMøteIdBlantForeldre)
          .medEtterLagerOppdatert(oppdatering => this.visMelding(i18n('Sak "{sak}" oppdatert (av {bruker})'), this.i18nArgumenter(oppdatering, oppdatering.modell.endretEntitet.id)))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilSaksreferatOppdatert() {
        this.opprettetEllerOppdatertSakHendelse<OppdateringmodellSakReferatOppdatert>(ModelloppdateringType.SAK_REFERAT_OPPDATERT)
          .medAbonnentoppslag(this.sakAbonnenter, hendelseModell => hendelseModell.sakId)
          .medAbonnentoppslag(this.møteAbonnenter, hendelseModell => hendelseModell.moeteId)
          .medEtterLagerOppdatert(oppdatering => this.visMelding(i18n('Referat for sak "{sak}" oppdatert (av {bruker})'), this.i18nArgumenter(oppdatering, oppdatering.modell.sakId)))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilSakFlyttetTilMøte() {
        //Først fjerne saken fra lageret
        this.slettetHendelse<OppdateringmodellSakFlyttetTilMøte>(ModelloppdateringType.SAK_FLYTTET_TIL_MØTE)
          .medAbonnentoppslag(this.sakAbonnenter, hendelseModell => hendelseModell.sakId)
          .medAbonnentoppslag(this.møteAbonnenter, hendelseModell => hendelseModell.opprinneligMoeteId)
          .medFørLagerOppdatert(oppdatering => {
              if (oppdatering.modell.sakId == this.aktivSakId) this.resetAktivSak();
              this.visMelding(i18n('Sak "{sak}" flyttet til annet møte (av {bruker})'), this.i18nArgumenter(oppdatering, oppdatering.modell.sakId))
          })
          .oppdaterLagerVedHendelse();
        //Så legge saken tilbake - med ny forelder
        //her lytter vi kun til møte (møte saken ble flyttet til), og ikke til saken.
        //Om vi lytter til saken også vil den flyttede saken bli levert til sak-visningssiden selvom den nå tilhørere et annet møte,
        //og det kan gi krøll
        this.opprettetEllerOppdatertSakHendelse<OppdateringmodellSakFlyttetTilMøte>(ModelloppdateringType.SAK_FLYTTET_TIL_MØTE)
          .medAbonnentoppslag(this.møteAbonnenter, hendelseModell => hendelseModell.moeteId)
          .medEtterLagerOppdatert(oppdatering => this.visMelding(i18n('Sak "{sak}" flyttet fra annet møte (av {bruker})'), this.i18nArgumenter(oppdatering, oppdatering.modell.sakId)))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilSakSlettet() {
        this.slettetHendelse<OppdateringmodellSakSlettet>(ModelloppdateringType.SAK_SLETTET)
          .medFørLagerOppdatert(oppdatering => this.visMelding(i18n('Sak "{sak}" slettet (av {bruker})'), this.i18nArgumenter(oppdatering, oppdatering.modell.sakId)))
          .oppdaterLagerVedHendelse();
    }

    private slettetHendelse<HENDELSE_MODELL_TYPE extends OppdateringmodellMedSak>(hendelseType: ModelloppdateringType) {
        return new ModellSlettetHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, AgendaelementModell>(this.sakLager, this.modelloppdateringer)
          .lyttTil(hendelseType)
          .medEntitetId(hendelseModell => hendelseModell.sakId);
    }

    private opprettetEllerOppdatertGeneriskHendelse<HENDELSE_MODELL_TYPE extends OppdateringmodellEntitetMedForelder>(hendelseType: ModelloppdateringType) {
        return new ModellOpprettetEllerOppdatertHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, AgendaelementModell>(this.sakLager, this.modelloppdateringer)
          .lyttTil(hendelseType)
          .medEntitetId(hendelseModell => hendelseModell.endretEntitet.id)
          .medEntitetLaster(hendelseModell => this.sakRessurs.hent(hendelseModell.endretEntitet.id).råRespons())
          .medForberedLageroppdatering(sak => leggTilForelderId(sak, this.forelderIdFraSak(sak)));
    }

    private opprettetEllerOppdatertSakHendelse<HENDELSE_MODELL_TYPE extends OppdateringmodellMedSak>(hendelseType: ModelloppdateringType) {
        return new ModellOpprettetEllerOppdatertHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, AgendaelementModell>(this.sakLager, this.modelloppdateringer)
          .lyttTil(hendelseType)
          .medEntitetId(hendelseModell => hendelseModell.sakId)
          .medEntitetLaster(hendelseModell => this.sakRessurs.hent(hendelseModell.sakId).råRespons())
          .medForberedLageroppdatering(sak => leggTilForelderId(sak, this.forelderIdFraSak(sak)));
    }

    /** returnerer objekt med 'sak' og 'bruker' felt populert fra angitt oppdatering  */
    private i18nArgumenter(oppdatering: Modelloppdatering<any>, sakId: AgendaelementId) {
        return i18nArgumenterMedAnsvarligBruker(oppdatering, {sak: this.sakstittel(sakId)});
    }

    /** Returnerer sak.tittel for sak registrert i sakLager, '...' dersom saken ikke finnes i sakLager */
    private sakstittel(sakId: AgendaelementId) {
        return this.sakLager.value.entities[sakId]?.tittel ?? '...';
    }

    private visMelding(melding: string, argumenter = {}) {
        this.meldingTilBruker.infomelding(this.oversettelser.oversett(melding, argumenter));
    }

}

type OppdateringmodellMedSak = { sakId: AgendaelementId };

/** Datalager-spesifikk representasjon av en sak der felter som inneholder data som håndteres av andre datalager er fjernet.
 * Dette da datalageret ikke garanterer at disse feltene er oppdatert.
 * Verdier for disse feltene må hentes via de respektive datalagrene for de aktuelle dataene. */
export type AgendaelementModellDLM = Omit<AgendaelementModell, 'kommentarer' | 'beslutninger' | 'avstemninger' | 'oppgaver' | 'dokumenter'>;
